import { defineStore } from 'pinia';
import { GET_IS_HAVE_SUGGESTIONS } from '#shared/types/getters';
import { FETCH_SEARCH_SUGGESTIONS, SET_SEARCH_SUGGESTION_DATA, SET_TOP_SUGGESTION, SET_SUGGESTION_ENGINE_USED } from '#shared/types/actions';
import { viewTypeToDeviceType } from '#shared/constants';
import useSearchSuggestions, { buildTopSuggestionLabel } from '~/composables/dumb/useSearchSuggestions';
import { securityValidation } from '#shared/utils/StringUtil';
import { useNavViewType } from './navViewType';

const MIN_SEARCH_VALUE_LENGTH = 2;

const { getSuggestions, parseSuggestions, getFromCache, getDepartmentFromCache } = useSearchSuggestions();

export const useNavSearchSuggestions = defineStore('navSearchSuggestions', {
  state: () => ({
    data: [],
    topSuggestion: null,
    suggestionEngineUsed: null,
  }),
  getters: {
    [GET_IS_HAVE_SUGGESTIONS](state) {
      return state.data.length !== 0;
    },
  },
  actions: {
    [SET_SEARCH_SUGGESTION_DATA](payload) {
      this.data = payload;
      this.topSuggestion = payload.length ? buildTopSuggestionLabel(payload[0]) : null;
    },
    [SET_TOP_SUGGESTION](payload) {
      this.topSuggestion = payload;
    },
    [SET_SUGGESTION_ENGINE_USED](payload) {
      this.suggestionEngineUsed = payload;
    },

    async [FETCH_SEARCH_SUGGESTIONS](value) {
      const pageData = usePageData();
      const envProps = useEnvProps();
      const navViewType = useNavViewType();

      const { isInternational } = pageData.navigation.context; // international does not have search suggestions

      if (value.length < MIN_SEARCH_VALUE_LENGTH || !securityValidation(value) || isInternational) {
        this.$reset();
        return;
      }
      const suggestionsFromCache = await getFromCache(value);
      const suggestionDepartmentsFromCache = await getDepartmentFromCache(value);

      if (suggestionsFromCache.value) {
        this[SET_SEARCH_SUGGESTION_DATA](
          parseSuggestions(
            {
              SUGGESTION: decodeURI(suggestionsFromCache.value).split(','),
              SuggestionWithDepartments: suggestionDepartmentsFromCache.value && JSON.parse(suggestionDepartmentsFromCache.value),
            },
            value,
          ),
        );
      } else {
        const { isMcom, hostlink } = envProps;
        const { regionCode } = pageData.navigation.context;
        const { viewType } = navViewType;
        const deviceType = viewTypeToDeviceType[viewType];

        const resultData = await getSuggestions({
          value,
          deviceType,
          regionCode,
          isMcom,
          hostlink,
        });

        if (!resultData) {
          this.$reset();
          return;
        }

        const parsedSuggestions = parseSuggestions(resultData || {}, value);
        this[SET_SEARCH_SUGGESTION_DATA](parsedSuggestions);
        this[SET_SUGGESTION_ENGINE_USED](resultData.suggestionEngineUsed);
        const { SUGGESTION = '', SuggestionWithDepartments = false } = resultData || {};
        suggestionsFromCache.value = encodeURI(SUGGESTION?.toString() || '');
        suggestionDepartmentsFromCache.value = SuggestionWithDepartments && JSON.stringify(SuggestionWithDepartments);
      }
    },
  },
});
